import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ilgiliMaddeler, rehberMaddesi, surumGecmisi } from '../api/rehber';
import { HataKutusu } from '../bilesenler/temel';
import { tarihMetni } from '../bicim/tarih';
import { TipRozeti } from './Rehber';

/**
 * TEK REHBER MADDESİ.
 *
 * KİP DEĞİL AYRI ROTA. Kontrolcü "derin bağlantıyla açılır" diyor: madde
 * adresi paylaşılabilir olmalı — bir itirazda "şu maddeye bakın" denebilmesi
 * gerekiyor. `Diyalog` kullanılsaydı adres taşınamaz, PWA da aynı içeriği
 * `?madde=` ile ikinci kez çözmek zorunda kalırdı.
 *
 * `{PARAM:KOD}` YER TUTUCULARI ZATEN ÇÖZÜLMÜŞ GELİR (`fn_rehber_parametre_coz`
 * `v_rehber` içinde). Burada ikinci bir ikame yapılmaz.
 */
export function RehberMaddesi() {
  const { kod } = useParams<{ kod: string }>();

  const madde = useQuery({
    queryKey: ['rehber', 'madde', kod],
    queryFn: () => rehberMaddesi(kod!),
    enabled: kod !== undefined,
  });

  const ilgili = useQuery({
    queryKey: ['rehber', 'ilgili', kod],
    queryFn: () => ilgiliMaddeler(kod!),
    enabled: kod !== undefined,
  });

  const surumler = useQuery({
    queryKey: ['rehber', 'surum-gecmisi', kod],
    queryFn: () => surumGecmisi(kod!),
    enabled: kod !== undefined,
  });

  if (madde.error) {
    return (
      <div className="space-y-4">
        <GeriBaglantisi />
        <HataKutusu hata={madde.error} />
      </div>
    );
  }

  if (madde.isPending || !madde.data) {
    return (
      <div className="space-y-4">
        <GeriBaglantisi />
        <p className="text-sm text-slate-500">Yükleniyor…</p>
      </div>
    );
  }

  const veri = madde.data;

  return (
    <div className="max-w-3xl space-y-6">
      <GeriBaglantisi />

      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <TipRozeti tip={veri.tip} />
          {veri.dogrulamaDurumu === 'beklemede' && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              Doğrulama bekliyor — dönem kilidini engeller
            </span>
          )}
          <span className="text-xs text-slate-500">
            {veri.kod} · sürüm {veri.surum} · {tarihMetni(veri.guncellemeTarihi)}
          </span>
        </div>

        <h1 className="text-xl font-semibold text-slate-900">{veri.baslik}</h1>

        {/*
          ESKİ BAŞLIK GÖSTERİLİR. Rehberin vaadi "kural değişti mi, ne zaman,
          neden"; başlık değiştiğinde eski adı arayan kullanıcı burada
          doğrulama yapabilmeli.
        */}
        {veri.oncekiBaslik !== null && (
          <p className="text-xs text-slate-500">Önceki başlık: {veri.oncekiBaslik}</p>
        )}
      </header>

      <Bolum baslik="Sistem ne yapıyor">
        <p className="whitespace-pre-line">{veri.sistemDavranisi}</p>
      </Bolum>

      {(veri.dayanakMadde !== null || veri.dayanakMetni !== null) && (
        <Bolum baslik="Mevzuat dayanağı">
          {veri.dayanakMadde !== null && (
            <p className="font-medium text-slate-900">{veri.dayanakMadde}</p>
          )}
          {veri.dayanakMetni !== null && (
            <p className="mt-1 whitespace-pre-line">{veri.dayanakMetni}</p>
          )}
        </Bolum>
      )}

      {/*
        ÖRNEK CANLI HESAP MOTORUNDAN ÜRETİLİR ve seed metnini EZER. Bir katsayı
        TİS ile değiştiğinde buradaki rakam da değişir; rehber ile kodun
        ayrışması yapısal olarak imkânsız.
      */}
      {veri.ornek !== null && (
        <Bolum baslik="Rakamlı örnek">
          <p className="whitespace-pre-line font-mono text-xs leading-relaxed text-slate-800">
            {veri.ornek}
          </p>
        </Bolum>
      )}

      {/*
        `tip="tercih"` olanlarda İKİSİ DE ZORUNLU — mevzuat sessiz kaldığında
        seçilen yorumun yanında REDDEDİLEN yorum da yazılır. Yalnız seçilen
        gösterilseydi belge savunma değil beyan olurdu.
      */}
      {(veri.tercihSebebi !== null || veri.karsiGorus !== null) && (
        <Bolum baslik="Mevzuat sessiz — neden bu yorum">
          {veri.tercihSebebi !== null && <p className="whitespace-pre-line">{veri.tercihSebebi}</p>}
          {veri.karsiGorus !== null && (
            <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                Reddedilen yorum
              </h3>
              <p className="mt-1 whitespace-pre-line text-sm">{veri.karsiGorus}</p>
            </div>
          )}
        </Bolum>
      )}

      <Bolum baslik="Kimi ilgilendirir">
        <p className="text-sm">
          {veri.hedefRol.length > 0 ? veri.hedefRol.join(' · ') : 'Belirtilmemiş'}
        </p>
        {veri.ilgiliParametre !== null && (
          <p className="mt-2 text-xs text-slate-500">
            Bağlı parametre: <code>{veri.ilgiliParametre}</code>
          </p>
        )}
      </Bolum>

      {/*
        "BUNU DA OKU" — bir sorunun parçaları farklı maddelerde durur. Çıkış
        basmamayı okuyanın düzeltmeyi ve puantör yolunu da görmesi gerekir;
        bağ olmadan kullanıcı cevabın yarısını alıp gider.
      */}
      <Bolum baslik="Bunu da okuyun">
        <HataKutusu hata={ilgili.error} />
        {ilgili.isPending ? (
          <p className="text-sm text-slate-500">Yükleniyor…</p>
        ) : (ilgili.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-slate-500">Bağlı madde yok.</p>
        ) : (
          <ul className="space-y-1">
            {ilgili.data?.map((i) => (
              <li key={i.kod}>
                <Link
                  to={`/rehber/madde/${encodeURIComponent(i.kod)}`}
                  className="text-sm text-kurum-700 hover:underline"
                >
                  {i.baslik}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Bolum>

      <Bolum baslik="Sürüm geçmişi">
        <HataKutusu hata={surumler.error} />
        {surumler.isPending ? (
          <p className="text-sm text-slate-500">Yükleniyor…</p>
        ) : (
          <ol className="space-y-3">
            {surumler.data?.map((s) => (
              <li
                key={s.surum}
                className={`border-l-2 pl-3 ${s.aktif ? 'border-kurum-500' : 'border-slate-200'}`}
              >
                <p className="text-xs text-slate-500">
                  {s.surum} · {tarihMetni(s.guncellemeTarihi)}
                  {s.aktif && <span className="ml-2 text-kurum-700">yürürlükte</span>}
                </p>
                {s.degisiklikNotu !== null && (
                  <p className="mt-0.5 text-sm text-slate-800">{s.degisiklikNotu}</p>
                )}
                <p className="mt-0.5 text-xs text-slate-600">{s.sistemDavranisi}</p>
              </li>
            ))}
          </ol>
        )}
      </Bolum>
    </div>
  );
}

function GeriBaglantisi() {
  return (
    <Link to="/rehber" className="text-sm text-kurum-700 hover:underline">
      ← Rehbere dön
    </Link>
  );
}

function Bolum({ baslik, children }: { baslik: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="text-sm font-semibold text-slate-800">{baslik}</h2>
      <div className="mt-2 text-sm leading-relaxed text-slate-700">{children}</div>
    </section>
  );
}
